import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';
import format from 'string-format';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import AntConfig from '../utils/AntConfig';

import AntLocalization from '../utils/AntLocalization';
import AntCondition from '../utils/AntCondition';
import AntArray from '../utils/AntArray';
import { AntGlobal, AntSession } from '../utils/AntContext';
import AntText from './AntText';
import AntProcessingButton from './AntProcessingButton';

import AntCardViewUtil from './AntCardViewUtil';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    // const session = AntSession();
    const location = useLocation();
    const navigate = useNavigate();

    const [cellData, setCellData] = useState(props.item);

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            let found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const CardUtil = useMemo(() => {
        let util = new AntCardViewUtil(config, tableName);
        return util;
    }, []);

    const getCellStyle = useCallback(
        (data) => {
            if (props.cardStyles instanceof Array)
                for (let i = 0; i < props.cardStyles.length; i++) {
                    const cellStyle = props.cardStyles[i];

                    if (cellStyle.when && typeof cellStyle.when === 'object') {
                        var isValid = AntCondition.isValid(data, {
                            when: cellStyle.when,
                            whenType: cellStyle.whenType
                        });

                        if (isValid) return cellStyle;
                    }
                }

            return {
                backgroundColor: '#3b64a9d9',
                textColor: '#fff'
            };
        },
        [props.cardStyles]
    );

    const getCellCssClasses = useCallback(
        (data) => {
            if (props.cardClasses instanceof Array)
                for (let i = 0; i < props.cardClasses.length; i++) {
                    const cssCls = props.cardClasses[i];

                    if (cssCls.when && typeof cssCls.when === 'object') {
                        var isValid = AntCondition.isValid(data, {
                            when: cssCls.when,
                            whenType: cssCls.whenType
                        });

                        if (isValid) return cssCls.value;
                    } else return cssCls.value;
                }
            return [];
        },
        [props.cardClasses]
    );

    const _renderRow = useCallback(
        ({ row, item, index }) => {
            var vCells = [];

            for (let i = 0; i < row.cells.length; i++) {
                let cell = row.cells[i];

                let vCellValue = item[cell.dataField];
                if (
                    cell.dataField &&
                    cell.hideWhenEmpty &&
                    (vCellValue === undefined || vCellValue === 0 || vCellValue === null || String(vCellValue).trim() === '')
                )
                    continue;

                let visible = CardUtil.isFieldVisible(props, cell, item);

                if (visible) {
                    var cnfTable = cell.cnfTable ? cell.cnfTable : tableName;
                    var cellCnf = CardUtil.initFieldConfig({
                        dataField: cell.dataField,
                        dataType: cell.dataType,
                        cnfTable: cnfTable,
                        cnfField: cell.cnfField
                    });

                    vCellValue = CardUtil.formatFieldValue(props, cell, item, vCellValue, cellCnf);

                    if (vCellValue !== undefined && vCellValue !== null && String(vCellValue).trim() !== '') {
                        let rawCell = JSON.parse(stringify(cell));
                        if (rawCell.icon instanceof Array) {
                            rawCell.icon = AntCondition.getValue(item, rawCell.icon);
                        }
                        vCells.push({
                            key: index + '-' + i,
                            tags: cell.tags ? cell.tags : [],
                            style: cell.style ? cell.style : {},
                            value: vCellValue,
                            rawCell: rawCell,
                            dataType: cellCnf.dataType
                        });
                    }
                }
            }

            if (vCells.length > 1 && !vCells[vCells.length - 1].rawCell.left) {
                // vCells[vCells.length - 1].tags.push("right");
                vCells[vCells.length - 1].rawCell.right = true;
            }

            let rowStyle = {};
            let cellStyle = getCellStyle(item);
            if (cellStyle && cellStyle.textColor) {
                rowStyle.color = cellStyle.textColor;
            }
            if (row.right) rowStyle.justifyContent = 'flex-end';
            if (row.center) rowStyle.justifyContent = 'center';

            return {
                vCells: vCells,
                content: (
                    // <div className="ant-card-row" key={index} row marginV-5 {...row.tags} style={{ ...row.style }} centerV>

                    <div className="ant-card-row" key={index} style={rowStyle}>
                        {vCells.map((v) => {
                            let style = {};
                            if (cellStyle) {
                                if (cellStyle.style) style = { ...style, ...cellStyle.style };

                                style.color = cellStyle.textColor;
                                if (v.rawCell.dataType === 'Array' || v.rawCell.displayAsTag) style.backgroundColor = cellStyle.backgroundColor;
                            }

                            let iFlex = AntCondition.getValue(item, v.rawCell.flex);
                            return (
                                <AntText
                                    key={v.key}
                                    {...v.rawCell}
                                    style={{ ...v.rawCell.style, flex: iFlex }}
                                    color={style.color}
                                    backgroundColor={style.backgroundColor}
                                >
                                    {AntLocalization.translateText(v.value)}
                                </AntText>
                            );
                        })}
                    </div>
                )
            };
        },
        [props, getCellStyle]
    );

    const _renderContent = useCallback(
        (args) => {
            var vRows = [];

            for (let i = 0; i < args.layout.rows.length; i++) {
                const row = args.layout.rows[i];
                let vRow = _renderRow({
                    row: row,
                    item: args.item,
                    index: i
                });
                if (vRow && vRow.vCells.length > 0) {
                    if (!args.controlHeight || !props.maxCellHeight || (rowsCount.current + 1) * 28 <= props.maxCellHeight) {
                        vRows.push(vRow.content);
                        rowsCount.current = rowsCount.current + 1;
                    }
                }
            }

            return {
                vRows: vRows,
                content: (
                    // <div className="ant-card-section" flex key={args.key}>
                    <div className="ant-card-section" key={tableName + '-' + args.key}>
                        {vRows.map((v) => v)}
                    </div>
                )
            };
        },
        [_renderRow, props.maxCellHeight, tableName]
    );

    const renderDetailLink = useCallback(
        (rowData) => {
            var ar = [];
            if (props.showDetail && props.showDetail instanceof Array) {
                for (let i = 0; i < props.showDetail.length; i++) {
                    const item = props.showDetail[i];
                    ar.push(item);
                }
            } else if (props.showDetail && typeof props.showDetail === 'object')
                ar.push({
                    when: props.showDetail.when,
                    whenType: props.showDetail.whenType,
                    url: props.showDetail.url,
                    key: props.showDetail.key
                });

            for (let i = 0; i < ar.length; i++) {
                const item = ar[i];

                if (item.when) {
                    var isValid = true;

                    var isValid = AntCondition.isValid(rowData, {
                        when: item.when,
                        whenType: item.whenType
                    });

                    if (!isValid) continue;
                }

                var url = item.url ? item.url : null;
                var key = null;

                if (url) {
                    if (item.key) {
                        key = item.key;
                        if (!rowData[key]) continue;
                    }

                    if (!key || !rowData[key]) key = 'code';
                    if (!key || !rowData[key]) key = 'id';
                    if (!key || !rowData[key]) key = props.keyExpr;

                     if (key && rowData[key]) {
                        var value = rowData[key];

                        var linkUrl = format('{0}/{1}', url, value);
                        return {
                            url: linkUrl,
                            value: value,
                            rowData: rowData
                        };
                    }
                }
            }

            return null;
        },
        [props.keyExpr, props.showDetail]
    );

    const initActionsList = useCallback(
        ({ cellActions, cData }) => {
            if (!cData) return [];

            var actions =
                cellActions instanceof Array
                    ? cellActions.filter(
                          (r) => r.code && cData.actions instanceof Array && cData.actions.some((a) => a.name === r.code && Boolean(a.visible))
                      )
                    : [];

            actions = actions.map((a) => {
                let strText = a.name instanceof Array ? AntCondition.getValue(cData, a.name) : a.name;
                let strIcon = a.icon;
                let strIconType = a.iconType;
                let stylingMode = a.stylingMode;
                let width = a.width;
                if (!a.name) {
                    if (['actionDelete', 'actionCancel'].includes(a.code)) {
                        stylingMode = 'text';
                        strIcon = 'trash';
                        strIconType = 'FontAwesome';
                        strText = { vi: 'Xóa', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएं', ar: 'حذف' };
                        width = 30;
                    }
                    if (['actionReject'].includes(a.code)) {
                        stylingMode = 'text';
                        strIcon = 'clear';
                        strIconType = 'FontAwesome';
                        strText = { vi: 'Từ chối', en: 'Reject', fr: 'Rejeter', es: 'Rechazar', id: 'Tolak', ja: '却下', zh: '拒绝', ko: '거부', hi: 'अस्वीकार', ar: 'رفض' };
                        width = 30;
                    }
                    if (['actionPost'].includes(a.code)) {
                        stylingMode = 'text';
                        strIcon = 'check';
                        strIconType = 'FontAwesome';
                        strText = { vi: 'Ghi nhận', en: 'Post', fr: 'Publier', es: 'Publicar', id: 'Posting', ja: '投稿', zh: '发布', ko: '게시', hi: 'पोस्ट', ar: 'نشر' };
                        width = 30;
                    }
                    if (['actionApprove'].includes(a.code)) {
                        stylingMode = 'text';
                        strIcon = 'check';
                        strIconType = 'FontAwesome';
                        strText = { vi: 'Duyệt', en: 'Approve', fr: 'Approuver', es: 'Aprobar', id: 'Setuju', ja: '承認', zh: '批准', ko: '승인', hi: 'स्वीकृत', ar: 'موافقة' };
                        width = 30;
                    }
                    if (['actionClose'].includes(a.code)) {
                        stylingMode = 'text';
                        strIcon = 'check';
                        strIconType = 'FontAwesome';
                        strText = { vi: 'Hoàn tất', en: 'Close', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' };
                        width = 30;
                    }
                    if (['actionRelease'].includes(a.code)) {
                        stylingMode = 'text';
                        strIcon = 'parentfolder';
                        strIconType = 'FontAwesome';
                        strText = { vi: 'Xác nhận', en: 'Release', fr: 'Libérer', es: 'Liberar', id: 'Rilis', ja: 'リリース', zh: '释放', ko: '해제', hi: 'रिलीज़', ar: 'إصدار' };
                        width = 30;
                    }
                }

                return {
                    ...a,
                    id: a.code,
                    text: AntLocalization.translateText(strText),
                    icon: strIcon,
                    iconType: strIconType,
                    type: a.type,
                    code: cData.code,
                    stylingMode: stylingMode,
                    width: (width ? width : 60) + 5
                };
            });

            if (actions.length > 0 && actions.some((a) => a.id === 'viewDetail' || a.insteadOfShowDetail) === false) {
                var lnk = renderDetailLink(cData);
                if (lnk && lnk.url) {
                    actions.push({
                        id: 'viewDetail',
                        text: AntLocalization.translateText({ vi: 'Chi tiết', en: 'Detail', fr: 'Détail', es: 'Detalle', id: 'Detail', ja: '詳細', zh: '详情', ko: '상세', hi: 'विवरण', ar: 'تفاصيل' }),
                        url: lnk.url,
                        code: cData.code
                    });
                }
            }

            if (props.excludesRowActions instanceof Array) {
                actions = actions.filter((a) => {
                    let isSome = props.excludesRowActions.some((e) => e === a.id || e?.code === a.id);

                    return !isSome;
                });
            }

            return actions;
        },
        [props.showDetail, renderDetailLink, props.excludesRowActions]
    );

    const rowsCount = useRef(0);

    const actionsList = useMemo(() => initActionsList({ cellActions: props.cellActions, cData: props.item }), []);

    const onAction = useCallback(
        async (e) => {
            let actionName = e.actionId;
            if (actionName === 'viewDetail' && e.action?.url && e.action?.code) {
                let url = String(e.action?.url);

                if (String(url).endsWith('/')) url = String(url).slice(0, -1);

                navigate(url);
                return;
            }

            let result;
            if (props.onAction) result = await props.onAction(e);
            if (result?.data?.id === cellData?.id) {
                setCellData(result?.data);
            }
        },
        [props.onAction, cellData]
    );

    const renderButtonsCell = useCallback(() => {
        if (actionsList instanceof Array === false || actionsList.length <= 0) return <div />;

        var actions =
            actionsList instanceof Array
                ? actionsList.filter(
                      (r) =>
                          r.id &&
                          (r.id === 'viewDetail' ||
                              (cellData.actions instanceof Array &&
                                  cellData.actions.some(
                                      (a) => a.name === r.id && Boolean(a.visible)
                                      //    && ['actionDelete', 'actionCancel'].includes(r.code) == false
                                  )))
                  )
                : [];

        actions = actions.map((a) => {
            let strText = a.text ? a.text : a.name instanceof Array ? AntCondition.getValue(cellData, a.name) : a.name;
            let strIcon = a.icon;
            let stylingMode = a.stylingMode;
            if (strText) {
                if (['actionDelete', 'actionCancel'].includes(a.code)) {
                    stylingMode = 'text';
                    strIcon = 'trash';
                    strText = '';
                }
                if (['actionReject'].includes(a.code)) {
                    stylingMode = 'text';
                    strIcon = 'clear';
                    strText = '';
                }
                if (['actionPost', 'actionApprove', 'actionClose'].includes(a.code)) {
                    stylingMode = 'text';
                    strIcon = 'check';
                    strText = '';
                }
                if (['actionRelease'].includes(a.code)) {
                    stylingMode = 'text';
                    strIcon = 'parentfolder';
                    strText = '';
                }
            }

            return {
                id: a.id,
                url: a.url,
                code: a.code,
                text: AntLocalization.translateText(strText),
                icon: strIcon,
                type: a.type,
                stylingMode: stylingMode
            };
        });

        if (!actions || actions.length <= 0) return <div key={cellData.id} />;
        return (
            <Toolbar className="ant-card-cell-buttons" key={cellData.id}>
                {actions.map((item, i) => {
                    return (
                        <Item key={i}>
                            <AntProcessingButton
                                action={item}
                                actionId={item.id}
                                type={item.type}
                                stylingMode={item.stylingMode}
                                icon={item.icon}
                                data={cellData}
                                text={AntLocalization.translateText(item.text)}
                                width={80}
                                onClick={onAction}
                            />
                        </Item>
                    );
                })}
            </Toolbar>
        );
    }, [cellData, props.cellActions, actionsList, onAction]);

    const renderCard = useCallback(() => {
        if (props.cellRender) return props.cellRender({ item: cellData });
        let header;
        let footer;
        let main;
        let actionPanel;

        rowsCount.current = 0;
        if (props.cardLayout.header)
            header = _renderContent({
                layout: props.cardLayout.header,
                item: cellData,
                key: 'header'
            });
        if (props.cardLayout.footer)
            footer = _renderContent({
                layout: props.cardLayout.footer,
                item: cellData,
                key: 'footer'
            });
        if (props.cardLayout.main)
            main = _renderContent({
                layout: props.cardLayout.main,
                item: cellData,
                key: 'main',
                controlHeight: true
            });

        if (actionsList && actionsList.length > 0) actionPanel = renderButtonsCell();

        var ar = [];
        if (header && header.vRows.length > 0) ar.push(header.content);
        if (main && main.vRows.length > 0) {
            // if (ar.length > 0) ar.push(<View key={ar.length} marginV-5 height={1} bg-lineLightColor />);

            ar.push(main.content);
        }
        if (footer && footer.vRows.length > 0) {
            // if (ar.length > 0) ar.push(<View key={ar.length} marginV-5 height={1} bg-lineLightColor />);
            ar.push(footer.content);
        }
        if (props.isSelected && actionPanel && actionsList && actionsList.length > 0) {
            // if (ar.length > 0) ar.push(<View key={ar.length} marginV-5 height={1} bg-lineLightColor />);
            ar.push(actionPanel);
        }

        let cardStyle = getCellStyle(cellData);
        let newClasses = getCellCssClasses(cellData);
        if (newClasses instanceof Array === false) newClasses = [newClasses];

        let classes = ['ant-card'];
        // if (props.isSelected) classes.push('ant-card-selected');
        // if (props.isSelected && props.hightLightSelectedItem) classes.push('ant-card-selected-marked');

        classes = [...classes, ...newClasses].filter((i) => Boolean(i)).join(' ');

        let newStyle = {
            backgroundColor: cardStyle?.backgroundColor
        };

        if (props.minCellHeight) newStyle.minHeight = props.minCellHeight;

        if (cardStyle?.style) newStyle = { ...newStyle, ...cardStyle.style };

        return (
            <div className={classes} key={tableName + '-' + cellData.id} style={newStyle}>
                <div className="ant-card-overlay"></div>
                <div className="ant-card-container">{ar.map((v) => v)}</div>
            </div>
        );
    }, [
        tableName,
        _renderContent,
        actionsList,
        renderButtonsCell,
        getCellStyle,
        getCellCssClasses,
        props.minCellHeight,
        props.isSelected,
        props.cellRender,
        props.cardLayout,
        cellData
    ]);

    const cardContent = useMemo(() => renderCard(), [renderCard, cellData]);

    useEffect(() => {
        setCellData(props.item);
    }, [props.item]);

    return <React.Fragment>{cardContent}</React.Fragment>;
});
